
import { useEffect, useState, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { City } from '../data/types/city-types';
import { createCityMarker } from '../components/map/markers/utils/city-marker-creation';

export const useCityMarkers = (
  map: mapboxgl.Map,
  cities: City[],
  selectedCity: City | null | undefined,
  onCityClick: (city: City) => void,
  showFunding: boolean = true
) => {
  const [markers, setMarkers] = useState<mapboxgl.Marker[]>([]);
  const markersCreatedRef = useRef(false);
  
  useEffect(() => {
    if (!map || !map.loaded()) return;

    console.log('useCityMarkers: Processing', cities.length, 'cities');

    // Always force cleanup of old markers for consistent behavior
    markers.forEach(marker => marker.remove());
    
    if (!cities.length) {
      setMarkers([]);
      markersCreatedRef.current = false;
      return;
    }
    
    // Reset marker created flag to ensure fresh markers on each region change
    markersCreatedRef.current = false;
    
    // Create new markers with fixed positioning
    const newMarkers = cities.map(city => {
      // Skip cities with invalid coordinates
      if (!city.coordinates || !Array.isArray(city.coordinates) || 
          city.coordinates.length !== 2 || 
          isNaN(city.coordinates[0]) || isNaN(city.coordinates[1])) {
        console.warn(`Invalid coordinates for city: ${city.name}`, city.coordinates);
        return null;
      }
      
      const isSelected = selectedCity ? (
        city.city_id === selectedCity.city_id || 
        city.id === selectedCity.id ||
        city.name === selectedCity.name
      ) : false;
      
      // Create marker with fixed anchor and offset
      const marker = createCityMarker(city, isSelected, onCityClick, showFunding);
      
      // Force immediate visibility
      const el = marker.getElement();
      el.style.transition = 'none';
      el.style.opacity = '1';
      el.style.visibility = 'visible';
      
      // Add data attributes for toggling visibility
      el.setAttribute('data-presence', city.church_presence || 'Unknown');
      
      // Determine if this is a low/unreached/need city
      if (city.church_presence === 'Low') {
        el.setAttribute('data-city-type', 'low');
      } else if (city.church_presence === 'Unreached') {
        el.setAttribute('data-city-type', 'unreached');
      } else if (city.population && city.population > 50000 && city.funding_amount && city.funding_amount < 10000) {
        el.setAttribute('data-city-type', 'need');
      }
      
      // Add a class to identify it as immediately visible
      el.classList.add('city-marker-immediate');
      
      // Set fixed position options
      marker.setOffset([0, 0]);
      
      // Immediately add to map for faster rendering
      marker.addTo(map);
      
      return marker;
    }).filter(Boolean) as mapboxgl.Marker[];
    
    console.log('useCityMarkers: Created', newMarkers.length, 'city markers');
    setMarkers(newMarkers);
    markersCreatedRef.current = true;
    
    return () => {
      newMarkers.forEach(marker => marker.remove());
    };
  }, [map, cities, selectedCity, onCityClick, showFunding]);
  
  return markers;
};
